import { VideoFormat } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Info } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface VideoFormatInfoProps {
  format: VideoFormat | null
  fileSize: number | null
  resolution: { width: number; height: number } | null
  duration: number | null
}

export function VideoFormatInfo({ 
  format, 
  fileSize, 
  resolution, 
  duration 
}: VideoFormatInfoProps) {
  // If no format information, don't render anything
  if (!format) return null;

  // Format file size in human-readable format
  const formatFileSize = (bytes: number | null): string => {
    if (bytes === null) return 'Unknown';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format duration in human-readable format
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return 'Unknown';
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${hrs > 0 ? `${hrs}h ` : ''}${mins}m ${secs}s`;
  };

  return (
    <div className="mb-3 px-3 py-2 bg-muted/50 rounded-md border border-muted-foreground/20">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          <span className="font-medium">{format.format}</span>
          <span className="text-xs">({format.description})</span>
        </div>
        
        <span className="text-muted-foreground/50 hidden sm:inline">•</span>
        
        {fileSize !== null && (
          <>
            <span className="text-muted-foreground">{formatFileSize(fileSize)}</span>
            <span className="text-muted-foreground/50 hidden sm:inline">•</span>
          </>
        )}
        
        {resolution !== null && (
          <>
            <span className="text-muted-foreground">{resolution.width}×{resolution.height}</span>
            <span className="text-muted-foreground/50 hidden sm:inline">•</span>
          </>
        )}
        
        {duration !== null && (
          <>
            <span className="text-muted-foreground">{formatDuration(duration)}</span>
            <span className="text-muted-foreground/50 hidden sm:inline">•</span>
          </>
        )}
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                {format.browserSupport.slice(0, 3).map((browser) => (
                  <Badge key={browser} variant="outline" className="text-xs py-0 px-1.5 h-5">
                    {browser}
                  </Badge>
                ))}
                {format.browserSupport.length > 3 && (
                  <Badge variant="outline" className="text-xs py-0 px-1.5 h-5">
                    +{format.browserSupport.length - 3}
                  </Badge>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Supported: {format.browserSupport.join(', ')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}